import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import ContactLog from "./ContactLog";

const LANGUAGE_LIST = [
  "Afrikaans", "Albanian", "Arabic", "Armenian", "Basque", "Bengali", "Bulgarian", "Catalan", "Cambodian", "Chinese (Mandarin)", "Croatian", "Czech", "Danish", "Dutch", "English", "Estonian",
  "Fiji", "Finnish", "French", "Georgian", "German", "Greek", "Gujarati", "Hebrew", "Hindi", "Hungarian", "Icelandic", "Indonesian", "Irish", "Italian", "Japanese", "Javanese", "Korean",
  "Latin", "Latvian", "Lithuanian", "Macedonian", "Malay", "Malayalam", "Maltese", "Maori", "Marathi", "Mongolian", "Nepali", "Norwegian", "Persian", "Polish", "Portuguese", "Punjabi",
  "Quechua", "Romanian", "Russian", "Samoan", "Serbian", "Slovak", "Slovenian", "Spanish", "Swahili", "Swedish", "Tamil", "Tatar", "Telugu", "Thai", "Tibetan", "Tonga", "Turkish",
  "Ukrainian", "Urdu", "Uzbek", "Vietnamese", "Welsh", "Xhosa",
];
const languageLevel = ['Native', 'Advanced', 'Proficiency', 'Basic'];
const REGION_LIST = ["Europe", "Middle East", "Asia Pacific", "Africa", "Latin America", "North America", "Global"];
const COUNTRY_LIST = [
  "Afghanistan", "Åland Islands", "Albania", "Algeria", "American Samoa", "Andorra", "Angola", "Anguilla", "Antarctica", "Antigua and Barbuda", 
  "Argentina", "Armenia", "Aruba", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bermuda", 
  "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Bouvet Island", "Brazil", "British Indian Ocean Territory", "Brunei Darussalam", "Bulgaria", "Burkina Faso", 
  "Burundi", "Cambodia", "Cameroon", "Canada", "Cape Verde", "Cayman Islands", "Central African Republic", "Chad", "Chile", "China", "Christmas Island", "Cocos (Keeling) Islands", 
  "Colombia", "Comoros", "Congo", "Congo, The Democratic Republic of The", "Cook Islands", "Costa Rica", "Cote D'ivoire", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", 
  "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Ethiopia", "Falkland Islands (Malvinas)", "Faroe Islands", 
  "Fiji", "Finland", "France", "French Guiana", "French Polynesia", "French Southern Territories", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Gibraltar", "Greece", "Greenland", 
  "Grenada", "Guadeloupe", "Guam", "Guatemala", "Guernsey", "Guinea", "Guinea-bissau", "Guyana", "Haiti", "Heard Island and Mcdonald Islands", "Holy See (Vatican City State)", "Honduras", 
  "Hong Kong", "Hungary", "Iceland", "India", "Indonesia", "Iran, Islamic Republic of", "Iraq", "Ireland", "Isle of Man", "Israel", "Italy", "Jamaica", "Japan", "Jersey", "Jordan", 
  "Kazakhstan", "Kenya", "Kiribati", "Korea, Democratic People's Republic of", "Korea, Republic of", "Kuwait", "Kyrgyzstan", "Lao People's Democratic Republic", "Latvia", "Lebanon", 
  "Lesotho", "Liberia", "Libyan Arab Jamahiriya", "Liechtenstein", "Lithuania", "Luxembourg", "Macao", "Macedonia, The Former Yugoslav Republic of", "Madagascar", "Malawi", "Malaysia", 
  "Maldives", "Mali", "Malta", "Marshall Islands", "Martinique", "Mauritania", "Mauritius", "Mayotte", "Mexico", "Micronesia, Federated States of", "Moldova, Republic of", "Monaco", 
  "Mongolia", "Montenegro", "Montserrat", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "Netherlands Antilles", "New Caledonia", "New Zealand", "Nicaragua", 
  "Niger", "Nigeria", "Niue", "Norfolk Island", "Northern Mariana Islands", "Norway", "Oman", "Pakistan", "Palau", "Palestinian Territory, Occupied", "Panama", "Papua New Guinea", 
  "Paraguay", "Peru", "Philippines", "Pitcairn", "Poland", "Portugal", "Puerto Rico", "Qatar", "Reunion", "Romania", "Russian Federation", "Rwanda", "Saint Helena", "Saint Kitts and Nevis", 
  "Saint Lucia", "Saint Pierre and Miquelon", "Saint Vincent and The Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", 
  "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Georgia and The South Sandwich Islands", "Spain", "Sri Lanka", "Sudan", 
  "Suriname", "Svalbard and Jan Mayen", "Swaziland", "Sweden", "Switzerland", "Syrian Arab Republic", "Taiwan, Province of China", "Tajikistan", "Tanzania, United Republic of", "Thailand", 
  "Timor-leste", "Togo", "Tokelau", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Turks and Caicos Islands", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", 
  "United Kingdom", "United States", "United States Minor Outlying Islands", "Uruguay", "Uzbekistan", "Vanuatu", "Venezuela", "Viet Nam", "Virgin Islands, British", "Virgin Islands, U.S.", 
  "Wallis and Futuna", "Western Sahara", "Yemen", "Zambia", "Zimbabwe"
];

export function Info({ expertData, firstContactLog, expertCity, expertRegion, expertCountry, languages, exAvailability, exAvailabilityComment, saveInfo, deleteLanguage, showStickyHeader }) {
  const [isEditing, setIsEditing] = useState(false);
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [country, setCountry] = useState("");
  const [languageList, setLanguageList] = useState(languages || []);
  const [newLanguage, setNewLanguage] = useState({ languageName: "", level: "" });
  const [availability, setAvailability] = useState("");
  const [availabilityComment, setAvailabilityComment] = useState("");

  useEffect(() => {
    setCity(expertCity || "");
    setRegion(expertRegion || "");
    setCountry(expertCountry || "");
    setAvailability(exAvailability);
    setAvailabilityComment(exAvailabilityComment || "");
  }, [expertCity, expertRegion, expertCountry, exAvailability, exAvailabilityComment]);

  const formatTime = (input) => {
    try {
      const date = new Date(input);
      if (isNaN(date)) return "";

      const datePart = date.toLocaleDateString("en-GB", {
        weekday: "long",
        year: "numeric",
        month: "short",
        day: "numeric",
      });

      const timePart = date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      return `${datePart}, ${timePart}`;
    } catch {
      return "";
    }
  };

  const formatLocation = () => {
    const parts = [city, country].filter(Boolean);
    return parts.length ? parts.join(", ") : "N/A";
  };

  const isPendingLanguageIncomplete = (newLanguage.languageName && !newLanguage.level) || (!newLanguage.languageName && newLanguage.level);
  const hasPendingLanguageComplete = newLanguage.languageName.trim() && newLanguage.level.trim();

  const handleAddLanguage = () => {
    if (!newLanguage.languageName.trim() || !newLanguage.level.trim()) return;

    const pendingLanguage = {
      languageName: newLanguage.languageName.trim(),
      level: newLanguage.level.trim(),
    };

    setLanguageList((prev) => [...prev, pendingLanguage]);
    setNewLanguage({ languageName: "", level: "" });
  };

  const dedupeLanguages = (items) => {
    const seen = new Set();
    return items.filter((item) => {
      const key = `${item.languageName}__${item.level}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const handleRemoveLanguage = (index) => {
    const removedLanguage = languageList[index];
    if (removedLanguage.ID) {
      deleteLanguage({
        language: removedLanguage
      });
    }
    setLanguageList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (isPendingLanguageIncomplete) return;

    const pendingLanguage = hasPendingLanguageComplete
      ? {
          languageName: newLanguage.languageName.trim(),
          level: newLanguage.level.trim(),
        }
      : null;

    const finalLanguages = dedupeLanguages(
      pendingLanguage ? [...languageList, pendingLanguage] : languageList
    );

    saveInfo({
      city,
      country,
      region,
      languages: finalLanguages,
    });

    setLanguageList(finalLanguages);
    setNewLanguage({ languageName: "", level: "" });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setCity(expertCity || "");
    setRegion(expertRegion || "");
    setCountry(expertCountry || "");
    setLanguageList(languages || []);
    setNewLanguage({ languageName: "", level: "" });
    setIsEditing(false);
  };

  useEffect(() => {
    setLanguageList(languages || []);
  }, [languages]);  

  return (
    <div
      className={`bg-white rounded-xl p-6 relative transition-all duration-300 ${isEditing ? "glow-burst glow-hold" : "shadow"}`}
    >
      {/* <div className="absolute top-0 right-0 w-10 h-10 bg-[#365561] rounded-bl-full"></div> */}
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-lg font-semibold mb-2">{!isEditing ? 'Info' : 'Edit Info'}</h3>
        {!isEditing ? (
          <button
            className="absolute top-5 right-6 text-gray-500 hover:text-black hover:bg-gray-200 py-1 px-2 rounded-full transition focus:outline-none"
            onClick={() => setIsEditing(true)}
            title="Edit Info"
          >
            <i className="fa-solid fa-pencil" />
          </button>
        ) : (
          <div className="absolute top-5 right-6 flex gap-2">
            <button
              className={`text-gray-500 hover:text-black py-1 px-2.5 rounded-full hover:bg-gray-200 focus:outline-none ${isPendingLanguageIncomplete ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={handleSave}
              title="Save"
              disabled={isPendingLanguageIncomplete}
            >
              <i className="fa-solid fa-floppy-disk" />
            </button>
            <button
              className="text-gray-500 hover:text-black py-1 px-2.5 rounded-full hover:bg-gray-200 focus:outline-none"
              onClick={handleCancel}
              title="Cancel"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {!isEditing ? (
          <motion.div
            key="info-view"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden text-sm text-gray-700 space-y-1"
          >
            <p>🌍 {formatLocation()}</p>
            <p>
              🗣️ {languageList.length > 0
                ? languageList.map((l) => (l.level ? `${l.languageName} (${l.level})` : `${l.languageName}`)).join(", ")
                : "No languages"}
            </p>
            <p>💾 Saved by {expertData.saved_by} on {formatTime(expertData.Profile_Created)}</p>
            <ContactLog expertID={expertData.id} firstContactLog={firstContactLog} showStickyHeader={showStickyHeader} />
            
          </motion.div>
        ) : (
          <motion.div
            key="info-edit"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden flex flex-col gap-4 text-sm"
          >
            <h3 className="text-sm text-gray-600 font-semibold">Location and Region</h3>
            <div className="flex gap-2 mb-3">
              <input
                className="w-1/3 border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none pb-1"
                placeholder="City"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
              <div className="w-1/3">
                <input
                  list="country-list"
                  className="w-full border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none pb-1"
                  placeholder="Country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
                <datalist id="country-list">
                  {COUNTRY_LIST.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>

              <div className="w-1/3">
                <input
                  list="region-list"
                  className="w-full border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none pb-1"
                  placeholder="Region"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                />
                <datalist id="region-list">
                  {REGION_LIST.map((r) => (
                    <option key={r} value={r} />
                  ))}
                </datalist>
              </div>
            </div>
            
            <h3 className="text-sm text-gray-600 font-semibold">Languages and Proficiency level</h3>
            <div className="flex flex-col gap-2 mb-3">
              {languageList.map((l, i) => (
                <div key={i} className="flex justify-between items-center gap-2">
                  <span>{l.level ? `${l.languageName} (${l.level})` : l.languageName}</span>
                  <button
                    className="text-red-500 hover:underline text-xs"
                    onClick={() => handleRemoveLanguage(i)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <div className="w-full">
                  <input
                    list="language-options"
                    placeholder="Language"
                    value={newLanguage.languageName}
                    onChange={(e) =>
                      setNewLanguage({ ...newLanguage, languageName: e.target.value })
                    }
                    className="w-full border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-1 bg-white"
                  />
                  <datalist id="language-options">
                    {LANGUAGE_LIST.map((lang) => (
                      <option key={lang} value={lang} />
                    ))}
                  </datalist>
                </div>

                <div className="w-2/6">
                  <input
                    list="language-levels"
                    placeholder="Level"
                    value={newLanguage.level}
                    onChange={(e) =>
                      setNewLanguage({ ...newLanguage, level: e.target.value })
                    }
                    className="w-full border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-1 bg-white"
                  />
                  <datalist id="language-levels">
                    {languageLevel.map((level) => (
                      <option key={level} value={level} />
                    ))}
                  </datalist>
                </div>
              </div>

              <button
                onClick={handleAddLanguage}
                disabled={!hasPendingLanguageComplete}
                className={`text-xs self-start focus:outline-none ${
                  hasPendingLanguageComplete
                    ? "text-blue-700 font-semibold"
                    : "text-gray-400 cursor-not-allowed"
                }`}
              >
                + Add New Language
              </button>

              {isPendingLanguageIncomplete && (
                <p className="text-xs text-amber-600">
                  Please select both language and level before saving.
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>
        {`
          @keyframes glowBurst {
            0% { box-shadow: 0 0 0px 0px rgba(54, 85, 97, 0); }
            30% { box-shadow: 0 0 20px 6px rgba(54, 85, 97, 0.6); }
            100% { box-shadow: 0 0 15px 3px rgba(54, 85, 97, 0.6); }
          }

          .glow-burst {
            animation: glowBurst 0.8s ease-out forwards;
            border-radius: 0.75rem;
          }

          .glow-hold {
            box-shadow: 0 0 20px 6px rgba(54, 85, 97, 0.6);
            border-radius: 0.75rem;
          }
        `}
      </style>
    </div>
  );
}